<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  activeSection: { type: String, required: true },
  activeList: { type: String, required: true },
  canCreate: { type: Boolean, default: false },
  listTabs: { type: Array, default: () => [] },
});

const emit = defineEmits(['selectSection', 'selectList', 'create']);
const { t } = useI18n();
const collapsed = ref(false);
const submenuOpen = ref(true);

const isOverviewActive = computed(() => props.activeSection === 'painel');
const isSalesActive = computed(() => props.activeSection === 'vendas');

const selectOverview = () => emit('selectSection', 'painel');
const selectSales = () => {
  if (!isSalesActive.value) {
    emit('selectList', props.activeList || 'rascunhos');
    return;
  }

  submenuOpen.value = !submenuOpen.value;
};
const selectList = listId => emit('selectList', listId);
</script>

<template>
  <aside
    data-testid="sales-index-navigation"
    class="flex h-screen w-[72px] shrink-0 flex-col overflow-hidden border-r border-[#E0E7EB] bg-white transition-[width] duration-700 sm:w-[106px] lg:w-auto"
    :class="collapsed ? 'lg:w-[106px]' : 'lg:w-[295px]'"
    :aria-label="t('SALES.ARIA.NAVIGATION')"
  >
    <div
      data-testid="sales-index-navigation-collapse-bar"
      class="flex h-[60px] shrink-0 items-center border-b border-[#E0E7EB]"
    >
      <button
        data-testid="sales-index-navigation-toggle"
        class="flex h-full w-full items-center justify-center rounded-[10px] text-[#01988C] transition hover:bg-[#EDF8F7]"
        type="button"
        :aria-expanded="!collapsed"
        :aria-label="
          collapsed
            ? t('SALES.ARIA.NAVIGATION_EXPAND')
            : t('SALES.ARIA.NAVIGATION_COLLAPSE')
        "
        @click="collapsed = !collapsed"
      >
        <span
          class="i-lucide-chevrons-left size-[22px]"
          :class="collapsed ? 'rotate-180' : ''"
          aria-hidden="true"
        />
      </button>
    </div>

    <nav
      data-testid="sales-index-navigation-list"
      class="flex-1 space-y-2 overflow-x-hidden overflow-y-auto p-2 lg:p-4"
    >
      <button
        v-if="props.canCreate"
        data-testid="sales-index-navigation-create"
        class="mb-2 flex h-14 w-full items-center justify-center gap-4 rounded-[10px] bg-[#01988C] px-[22px] py-1 text-left text-base font-bold text-white transition hover:bg-[#017F75]"
        type="button"
        @click="emit('create')"
      >
        <span
          class="i-heroicons-plus-solid size-5 shrink-0"
          aria-hidden="true"
        />
        <span v-if="!collapsed" class="hidden min-w-0 truncate lg:inline">
          {{ t('SALES.ACTIONS.CREATE_NAV') }}
        </span>
      </button>

      <button
        data-testid="sales-index-navigation-overview"
        class="flex h-14 w-full items-center justify-center gap-0 rounded-[10px] px-0 py-1 text-left text-base font-bold transition hover:bg-[#EDF8F7] lg:justify-start lg:gap-4 lg:px-[22px]"
        :class="
          isOverviewActive ? 'bg-[#EDF8F7] text-[#01988C]' : 'text-[#464F53]'
        "
        type="button"
        :aria-current="isOverviewActive ? 'page' : undefined"
        @click="selectOverview"
      >
        <span
          class="i-heroicons-home-solid size-5 shrink-0 text-[#01988C]"
          aria-hidden="true"
        />
        <span v-if="!collapsed" class="hidden min-w-0 truncate lg:inline">
          {{ t('SALES.SECTIONS.OVERVIEW') }}
        </span>
      </button>

      <button
        data-testid="sales-index-navigation-sales"
        class="flex h-14 w-full items-center justify-center gap-0 rounded-[10px] px-0 py-1 text-left text-base font-bold transition hover:bg-[#EDF8F7] lg:justify-start lg:gap-4 lg:px-[22px]"
        :class="
          isSalesActive ? 'bg-[#EDF8F7] text-[#01988C]' : 'text-[#464F53]'
        "
        type="button"
        :aria-expanded="!collapsed ? submenuOpen : undefined"
        :aria-current="isSalesActive ? 'page' : undefined"
        @click="selectSales"
      >
        <span
          class="i-heroicons-building-office-2-solid size-5 shrink-0 text-[#01988C]"
          aria-hidden="true"
        />
        <span
          v-if="!collapsed"
          class="hidden min-w-0 flex-1 truncate lg:inline"
        >
          {{ t('SALES.SECTIONS.SALES') }}
        </span>
        <span
          v-if="!collapsed"
          class="hidden size-[22px] shrink-0 text-[#01988C] lg:block"
          :class="submenuOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up'"
          aria-hidden="true"
        />
      </button>

      <ul
        v-if="!collapsed && submenuOpen"
        data-testid="sales-index-navigation-submenu"
        class="hidden list-none space-y-1 py-2 pl-[37px] lg:block"
      >
        <li v-for="item in props.listTabs" :key="item.id">
          <button
            :data-testid="`sales-index-navigation-${item.id}`"
            class="flex h-14 w-full items-center gap-2 rounded-[10px] px-[22px] py-1 text-left text-base font-bold text-[#464F53] transition hover:bg-[#EDF8F7]"
            :class="
              props.activeList === item.id && isSalesActive
                ? 'bg-[#EDF8F7] text-[#01988C]'
                : ''
            "
            type="button"
            :aria-current="
              props.activeList === item.id && isSalesActive ? 'page' : undefined
            "
            @click="selectList(item.id)"
          >
            <span class="min-w-0 truncate">{{ item.label }}</span>
            <span
              v-if="Number(item.badge) > 0"
              class="shrink-0 text-sm font-normal"
            >
              {{ t('SALES.ACTIONS.ORDER_COUNT', { count: item.badge }) }}
            </span>
          </button>
        </li>
      </ul>
    </nav>
  </aside>
</template>
